/* eslint-disable no-unused-vars */
/* eslint-disable no-restricted-syntax */
/* eslint-disable import/prefer-default-export */
import _ from "lodash";
import isEmpty from "lodash/isEmpty";
import isArray from "lodash/isArray";
import isObject from "lodash/isObject";
import uniqBy from "lodash/uniqBy";
import map from "lodash/map";
import { AR_PLATFORM, XR_MODES, ACTION_TYPES } from "../../../constants";

const modes = [XR_MODES.XR, XR_MODES.AR];
const modeList = {
  [XR_MODES.XR]: [AR_PLATFORM.THREEJS],
  [XR_MODES.AR]: [AR_PLATFORM.EIGHTHWALL, AR_PLATFORM.WEBXR],
};

const onLoadSortOrder = [
  ACTION_TYPES.OPACITY,
  ACTION_TYPES.MATERIALS,
  ACTION_TYPES.DYNAMIC_ANIMATION,
  ACTION_TYPES.CLIP_ANIMATION,
];

/**
 * Description:
 * The function uses the lodash function to perform the followinf actions in order
 *    1. picks all the parent objects from the `objData`
 *    2. Removes Empty object in the child object using recursive call
 *    3. Omits all the empty parent objects
 *    4. finally assigns the value
 * @param {Object} objData
 * @returns {Object}
 */
const removeEmptyObjects = (objData) => {
  const finalObj = {};
  Object.keys(objData).forEach((key) => {
    if (isObject(objData[key])) {
      const nestedObj = removeEmptyObjects(objData[key]);
      if (Object.keys(nestedObj).length > 0) {
        finalObj[key] = nestedObj;
      }
    } else if (
      objData[key] !== "" &&
      objData[key] !== undefined &&
      objData[key] !== null
    ) {
      finalObj[key] = objData[key];
    }
  });
  return finalObj;
};

/**
 * Description
 * The function is to sort the data in the array based on the `sortOrder` param and removes other data
 * @param {Array} data = [{ type: 'type', config: {} }]
 * @param {Array} sortOrder= ['type1', 'type2']
 * @returns {Array} Sorted Array
 */
const sortAndFilterData = (data, sortOrder = onLoadSortOrder) => {
  const sortedConfig = [];
  sortOrder.forEach((actionType) => {
    const actionConfig = data.find((e) => e.type === actionType);
    if (actionConfig && actionConfig?.config) sortedConfig.push(actionConfig);
  });
  return sortedConfig;
};

export const removeEmptyActions = (actionObj) => {
  const currentConfig = [];
  map(actionObj, (item) => {
    if (item && !isEmpty(item.config)) {
      const subItem = removeEmptyObjects(item.config);
      if (!isEmpty(subItem)) {
        currentConfig.push({ type: item.type, config: subItem });
      }
    }
  });
  return uniqBy(currentConfig, "type");
};

export const removeEmptySelections = (config) => {
  const configCheckList = ["onClick", "onUnClick"];
  const finalConfig = {};
  map(configCheckList, (objKey) => {
    const currentConfig = [];
    if (config[objKey]?.length > 0) {
      finalConfig[objKey] = removeEmptyActions(config[objKey]);
    }
  });

  return finalConfig;
};

export const getConfigByPlatform = (config, defaultConfig) => {
  if (isEmpty(config)) return defaultConfig;
  const xrConfig = config[XR_MODES.XR];
  const arConfig = config[XR_MODES.AR];

  if (!isEmpty(xrConfig)) {
    if (
      isArray(xrConfig[AR_PLATFORM.THREEJS]) &&
      xrConfig[AR_PLATFORM.THREEJS]?.length > 0
    ) {
      return xrConfig[AR_PLATFORM.THREEJS];
    }
    if (!isEmpty(xrConfig[AR_PLATFORM.THREEJS])) {
      return xrConfig[AR_PLATFORM.THREEJS];
    }
  }

  if (!isEmpty(arConfig)) {
    if (
      isArray(arConfig[AR_PLATFORM.EIGHTHWALL]) &&
      arConfig[AR_PLATFORM.EIGHTHWALL]?.length > 0
    ) {
      return arConfig[AR_PLATFORM.EIGHTHWALL];
    }
    if (!isEmpty(arConfig[AR_PLATFORM.EIGHTHWALL])) {
      return arConfig[AR_PLATFORM.EIGHTHWALL];
    }

    if (
      isArray(arConfig[AR_PLATFORM.WEBXR]) &&
      arConfig[AR_PLATFORM.WEBXR]?.length > 0
    ) {
      return arConfig[AR_PLATFORM.WEBXR];
    }
    if (!isEmpty(arConfig[AR_PLATFORM.WEBXR])) {
      return arConfig[AR_PLATFORM.WEBXR];
    }
    return defaultConfig;
  }

  return defaultConfig;
};

export const createFinalOnLoadConfig = (onLoadConfig) => {
  const individualConfig = getConfigByPlatform(onLoadConfig, []);
  const updatedConfig = removeEmptyActions(individualConfig);

  return {
    [XR_MODES.XR]: { [AR_PLATFORM.THREEJS]: sortAndFilterData(updatedConfig) },
    [XR_MODES.AR]: {
      [AR_PLATFORM.EIGHTHWALL]: sortAndFilterData(
        updatedConfig,
        onLoadSortOrder.slice(0, 2)
      ),
      [AR_PLATFORM.WEBXR]: sortAndFilterData(
        updatedConfig,
        onLoadSortOrder.slice(0, 2)
      ),
    },
  };
};

export const updateOnLoadConfig = (
  config,
  data,
  configType,
  isOnlyXr = true
) => {
  const updatedConfig = { ...config };
  if (isEmpty(config)) {
    updatedConfig[XR_MODES.XR] = { [AR_PLATFORM.THREEJS]: [data] };
    updatedConfig[XR_MODES.AR] = {
      [AR_PLATFORM.WEBXR]: [data],
      [AR_PLATFORM.EIGHTHWALL]: [data],
    };
  }

  for (const currentMode of modes) {
    if (!config[currentMode]) updatedConfig[currentMode] = {};
    for (const type of modeList[currentMode]) {
      const currentIndividualConfig = config[currentMode]
        ? config[currentMode][type]
        : [];
      if (isOnlyXr && currentMode !== XR_MODES.XR) break;
      if (!currentIndividualConfig) updatedConfig[currentMode][type] = [];
      if (currentIndividualConfig?.length === 0) {
        updatedConfig[currentMode][type] = [data];
      } else {
        const configIndex = currentIndividualConfig?.findIndex(
          (e) => e.type === configType
        );
        if (configIndex > -1) {
          updatedConfig[currentMode][type][configIndex] = data;
        } else {
          updatedConfig[currentMode][type].push(data);
        }
      }
    }
  }

  return updatedConfig;
};

export const updatePlatformConfig = (config, data, isOnlyXr = true) => {
  const updatedConfig = config;
  if (isEmpty(config)) {
    updatedConfig[XR_MODES.XR] = { [AR_PLATFORM.THREEJS]: { data } };
    updatedConfig[XR_MODES.AR] = {
      [AR_PLATFORM.WEBXR]: { data },
      [AR_PLATFORM.EIGHTHWALL]: { data },
    };
  }

  for (const currentMode of modes) {
    if (!updatedConfig[currentMode]) updatedConfig[currentMode] = {};
    for (const type of modeList[currentMode]) {
      if (isOnlyXr && currentMode !== XR_MODES.XR) break;
      if (!updatedConfig[currentMode][type]) {
        updatedConfig[currentMode][type] = {};
      } else {
        updatedConfig[currentMode][type] = {
          ...data,
        };
      }
    }
  }

  return updatedConfig;
};
